// Student panel sidebar component

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    BookOutlined,
    FileTextOutlined,
    LogoutOutlined,
    ProfileOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import {
    Calendar,
    CalendarCheck,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Home,
    Menu,
    MessageSquareText,
    Sparkles,
    TrendingUp,
} from "lucide-react";
import PropTypes from "prop-types";
import { SiSololearn } from "react-icons/si";
import { ImMeter } from "react-icons/im";

// Rename the file in /src/Assets to HE.AI-Logo.png (no spaces) and keep this import:
import HeAiLogo from "../Assets/HE.AI-Logo.png";
import { API_CONFIG } from "../config/api";

const API_BASE_URL = API_CONFIG.BASE_URL;

// Color constants — kept exactly as-is
const COLORS = {
    primary: "#F17C45",
    secondary: "#4365FD",
    background: "#0F172A",
    cardBg: "#1E293B",
    activeBg: "#334155",
    text: "#E2E8F0",
    textSecondary: "#94A3B8",
    accent: "#F59E0B",
    border: "#334155",
};

// Static groups (grade-conditional items are injected at runtime via buildGroups)
const buildGroups = (isSecondaryStage) => [
    {
        key: "overview",
        label: "Overview",
        emoji: "🏠",
        items: [
            { text: "Home", icon: <Home size={20} />, path: "/HomePage", key: "home" },
        ],
    },
    {
        key: "academics",
        label: "Learning",
        emoji: "📚",
        items: [
            { text: "My Classes", icon: <SiSololearn />, path: "/class-management", key: "class" },
            { text: "My Daily Learnings", icon: <BookOutlined />, path: "/my-lessons", key: "lessons" },
            { text: "My Assessments", icon: <FileTextOutlined />, path: "/assessments", key: "assessments" },
            { text: "Course Content", icon: <BookOutlined />, path: "/CoursesPage", key: "courses" },
            { text: "My Learnings", icon: <QuestionCircleOutlined />, path: "/QuizPage", key: "learnings" },
            { text: "Oral Learnings", icon: <QuestionCircleOutlined />, path: "/Oral", key: "learnings1" },
        ],
    },
    {
        key: "growth",
        label: "Growth & reports",
        emoji: "🌱",
        items: [
            { text: "Growth Quizzes", icon: <Sparkles size={20} />, path: "/growth-quizzes", key: "growth-quizzes" },
            { text: "Reflections", icon: <MessageSquareText size={20} />, path: "/reflections", key: "reflections" },
            { text: "Growth Report", icon: <TrendingUp size={20} />, path: "/growth-report", key: "growth-report" },
            { text: "Official Reports", icon: <FileTextOutlined />, path: "/official-growth-achievement", key: "official-growth-achievement" },
            { text: "Annual Academic Record", icon: <FileTextOutlined />, path: "/official-academic-transcript", key: "official-academic-transcript" },
        ],
    },
    {
        key: "progress",
        label: "Student progress",
        emoji: "📈",
        items: [
            { text: "My Holistic Progress", icon: <FileTextOutlined />, path: "/Parakh", key: "parakh" },
            { text: "My Competencies", icon: <ImMeter />, path: "/my-competencies", key: "competencies" },
            { text: "Career Compass", icon: <ImMeter />, path: "/carrier", key: "carrier" },
        ],
    },
    {
        key: "planning",
        label: "Planning",
        emoji: "🗓️",
        items: [
            { text: "My Calendar", icon: <Calendar />, path: "/my-calendar", key: "calendar" },
            { text: "My Attendance", icon: <CalendarCheck />, path: "/my-attendance", key: "attendance" },
        ],
    },
    {
        key: "personal",
        label: "Account",
        emoji: "👤",
        items: [
            // Grade-conditional items — included only for secondary stage (9-12)
            ...(isSecondaryStage
                ? [
                    {
                        text: "HPC Profile",
                        icon: <ProfileOutlined />,
                        path: "/hpc-profile",
                        key: "hpc-profile",
                    },
                    {
                        text: "Secondary Activities",
                        icon: <FileTextOutlined />,
                        path: "/secondary-domain-activities",
                        key: "domain-activities",
                    },
                ]
                : []),
            { text: "My Profile", icon: <ProfileOutlined />, path: "/Profile", key: "profile" },
            { text: "Log Out", icon: <LogoutOutlined />, path: "logout", key: "logout" },
        ],
    },
];

const findActiveGroup = (groups, pathname) => {
    for (const group of groups) {
        if (group.items.some((item) => item.path === pathname)) {
            return group.key;
        }
    }
    return null;
};

const buildInitialOpenGroups = (groups) =>
    groups.reduce((acc, group) => {
        acc[group.key] = true;
        return acc;
    }, {});

const Sidebar = ({ setCollapsed, collapsed }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [stageData, setStageData] = useState(null);
    const [stageLoading, setStageLoading] = useState(true);
    const [stageError, setStageError] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [currentCollapsed, setCurrentCollapsed] = useState(collapsed);
    const [isMobile, setIsMobile] = useState(false);
    const [openGroups, setOpenGroups] = useState({});

    // Keep local collapsed state in sync with parent prop
    useEffect(() => {
        setCurrentCollapsed(collapsed);
    }, [collapsed]);

    // Check if mobile view based on screen width
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, [currentCollapsed]);

    // Fetch the user's educational stage on component mount
    useEffect(() => {
        const fetchUserStage = async () => {
            try {
                setStageLoading(true);
                const accessToken = localStorage.getItem("accessToken");

                if (!accessToken) {
                    setStageError("Authentication required");
                    setStageLoading(false);
                    return;
                }

                const response = await fetch(`${API_BASE_URL}/user/stage`, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`Error fetching stage: ${response.status}`);
                }

                const data = await response.json();
                setStageData(data);
                setStageLoading(false);
            } catch (error) {
                console.error("Failed to fetch user stage:", error);
                setStageError(error.message || "Failed to load educational stage");
                setStageLoading(false);
            }
        };

        fetchUserStage();
    }, []);

    // Re-initialise open groups whenever stageData resolves (groups may change)
    const isSecondaryStage =
        stageData &&
        stageData.grade &&
        ["9", "10", "11", "12"].includes(stageData.grade);

    const groups = buildGroups(isSecondaryStage);

    useEffect(() => {
        if (!stageLoading) {
            const initial = buildInitialOpenGroups(groups);
            setOpenGroups(initial);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stageLoading]);

    // Auto-expand the group that contains the active route
    useEffect(() => {
        const activeGroupKey = findActiveGroup(groups, location.pathname);
        if (activeGroupKey) {
            setOpenGroups((prev) => ({ ...prev, [activeGroupKey]: true }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, stageLoading]);

    const toggleGroup = (groupKey) => {
        setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    };

    const handleLogout = () => {
        localStorage.removeItem("accessToken");
        navigate("/");
    };

    const handleClick = (path, text) => {
        if (text === "Log Out") {
            handleLogout();
        } else {
            navigate(path);
            if (isMobile) {
                setCurrentCollapsed(true);
                setCollapsed(true);
            }
        }
    };

    const handleCollapseClick = () => {
        const newCollapsed = !currentCollapsed;
        setCurrentCollapsed(newCollapsed);
        setCollapsed(newCollapsed);
    };

    const isActive = (path) => location.pathname === path;

    const renderItem = (item) => {
        const active = isActive(item.path);
        const isHovered = hoveredItem === item.key;

        return (
            <div
                key={item.key}
                onClick={() => handleClick(item.path, item.text)}
                onMouseEnter={() => setHoveredItem(item.key)}
                onMouseLeave={() => setHoveredItem(null)}
                className={`
                    group relative flex items-center gap-4 px-3 py-3
                    rounded-xl cursor-pointer transition-all duration-200
                    ${active
                        ? "bg-gradient-to-r from-orange-500/20 to-orange-600/20 border border-orange-500/30"
                        : isHovered
                            ? "bg-slate-800/60 border border-slate-600/50"
                            : "border border-transparent hover:bg-slate-800/40"
                    }
                    transform hover:scale-105 hover:shadow-lg hover:shadow-orange-500/10
                    ${currentCollapsed ? "justify-center" : ""}
                `}
            >
                {/* Active indicator */}
                {active && (
                    <div
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 w-1 h-8 rounded-r-full"
                        style={{ backgroundColor: COLORS.primary }}
                    />
                )}

                {/* Icon */}
                <div
                    className={`
                        flex items-center justify-center w-8 h-8
                        ${active ? "text-orange-400" : isHovered ? "text-blue-400" : "text-slate-400"}
                        transition-colors duration-200
                    `}
                >
                    {item.icon}
                </div>

                {/* Label */}
                {!currentCollapsed && (
                    <span
                        className={`
                            text-sm font-medium transition-colors duration-200
                            ${active ? "text-orange-300" : isHovered ? "text-blue-300" : "text-slate-300"}
                        `}
                    >
                        {item.text}
                    </span>
                )}

                {/* Tooltip for collapsed (icon-only) state */}
                {currentCollapsed && (
                    <div
                        className={`
                            absolute left-full ml-3 px-3 py-2 bg-slate-800 text-white text-sm
                            rounded-lg shadow-lg opacity-0 group-hover:opacity-100
                            transition-all duration-200 pointer-events-none z-50
                            border border-slate-600 whitespace-nowrap
                            ${isHovered ? "translate-x-0" : "translate-x-2"}
                        `}
                    >
                        {item.text}
                        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-slate-800 rotate-45 border-l border-b border-slate-600" />
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {/* Backdrop for mobile */}
            {isMobile && !currentCollapsed && (
                <div
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={() => {
                        setCurrentCollapsed(true);
                        setCollapsed(true);
                    }}
                />
            )}

            <div
                className={`
                    fixed top-0 left-0 h-screen bg-slate-900 text-slate-200 font-[poppins]
                    transition-all duration-300 ease-in-out
                    ${isMobile
                        ? `z-50 ${currentCollapsed ? "-translate-x-full" : "translate-x-0"} w-64`
                        : `z-50 ${currentCollapsed ? "w-20" : "w-64"}`
                    }
                    flex flex-col
                    border-r border-slate-700
                    shadow-2xl shadow-slate-900/50
                `}
                style={{ backgroundColor: COLORS.background }}
            >
                {/* Header / Logo Section */}
                <div
                    className="flex items-center justify-center px-4 py-4 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-900"
                >
                    <button
                        onClick={() => navigate("/")}
                        className="flex items-center justify-center"
                        aria-label="Go to Home"
                        title="Home"
                    >
                        <img
                            src={HeAiLogo}
                            alt="HE.AI"
                            className={`${currentCollapsed ? "h-10 w-10 object-contain rounded-md" : "h-10 w-auto"} drop-shadow-md`}
                            style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))" }}
                        />
                    </button>
                </div>

                {/* Stage Information (expanded state only) */}
                {!currentCollapsed && stageData && !stageError && (
                    <div className="px-6 font-[poppins] py-4 border-b border-slate-700">
                        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-3 border border-blue-500/20">
                            <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Current Stage</div>
                            <div className="text-sm font-semibold text-blue-400">
                                Grade {stageData.grade} - {stageData.stage}
                            </div>
                        </div>
                    </div>
                )}

                {/* Menu — grouped when expanded, flat icons-only when collapsed */}
                <div className="flex-1 font-[poppins] px-3 py-4 overflow-y-auto [-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden">
                    {groups.map((group) => {
                        const isOpen = openGroups[group.key] !== false;

                        return (
                            <div key={group.key} className="mb-1">
                                {/* Group header — only when sidebar is expanded */}
                                {!currentCollapsed && (
                                    <div
                                        onClick={() => toggleGroup(group.key)}
                                        style={{
                                            paddingLeft: 12,
                                            paddingRight: 8,
                                            paddingTop: 16,
                                            paddingBottom: 4,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <span style={{ fontSize: 14 }}>{group.emoji}</span>
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.08em",
                                                    color: "#64748b",
                                                }}
                                            >
                                                {group.label}
                                            </span>
                                        </div>
                                        <ChevronDown
                                            size={12}
                                            style={{
                                                color: "#64748b",
                                                transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
                                                transition: "transform 0.2s",
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Group items */}
                                <div className="space-y-1">
                                    {/* In collapsed mode show all items flat; in expanded mode respect isOpen */}
                                    {(currentCollapsed || isOpen) &&
                                        group.items.map((item) => renderItem(item))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Collapse Toggle Button */}
                <div className="border-t border-slate-700 p-4">
                    <button
                        onClick={handleCollapseClick}
                        className={`
                            w-full flex items-center justify-center gap-3 px-4 py-3
                            bg-gradient-to-r from-slate-800 to-slate-700
                            hover:from-slate-700 hover:to-slate-600
                            border border-slate-600 hover:border-slate-500
                            rounded-xl transition-all duration-200
                            text-slate-300 hover:text-orange-300
                            shadow-lg hover:shadow-xl font-[poppins]
                            transform hover:scale-105
                            group
                        `}
                    >
                        <div className="flex items-center justify-center">
                            {currentCollapsed ? (
                                <ChevronRight size={18} className="group-hover:text-orange-400 transition-colors" />
                            ) : (
                                <>
                                    <ChevronLeft size={18} className="group-hover:text-orange-400 transition-colors" />
                                    <span className="text-sm font-medium ml-2">Collapse</span>
                                </>
                            )}
                        </div>
                    </button>
                </div>

                {/* Gradient overlay for visual depth */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/20 pointer-events-none" />

                <style jsx>{`
                    @keyframes slideInFromLeft {
                        from {
                            opacity: 0;
                            transform: translateX(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                `}</style>
            </div>

            {/* Mobile menu toggle — only shown when sidebar is collapsed on mobile */}
            {isMobile && currentCollapsed && (
                <button
                    className="fixed top-6 left-6 z-[60] rounded-full w-12 h-12 flex items-center justify-center shadow-lg border border-slate-600"
                    style={{ background: COLORS.background, color: COLORS.primary }}
                    onClick={() => {
                        setCurrentCollapsed(false);
                        setCollapsed(false);
                    }}
                >
                    <Menu size={24} />
                </button>
            )}
        </>
    );
};

// PropTypes for type checking
Sidebar.propTypes = {
    setCollapsed: PropTypes.func.isRequired,
    collapsed: PropTypes.bool.isRequired,
};

export default Sidebar;
